import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { Trans } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import backButton from '../../assets/back-button-auth.svg'

const useStyles = createUseStyles({
  closeIconContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  backButton: {
    background: 'none',
    border: 'none',
    padding: 0,
  },
  title: {
    color: '#ffffff',
    fontFamily: serifFontFamily,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 22,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  privacyMessage: {
    color: '#ffffff',
    fontFamily: sansSerifFontFamily,
    fontWeight: 900,
    fontSize: 16,
    lineHeight: '160%',
    paddingBottom: 16,
  },
  message: {
    fontStyle: sansSerifFontFamily,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '187%',
    color: 'rgba(255, 255, 255, 0.56)',
    paddingLeft: 30,
    paddingRight: 30,
  },
  bold: {
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.56)',
  },
  resend: {
    paddingTop: 25,
  },
  linkText: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontWeight: 900,
    color: '#CBA67A',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  backToLogin: {
    marginTop: 25,
  },
  otherAccount: {
    paddingTop: 25,
  },
})

type EmailType = 'verification' | 'passwordReset'

interface Props {
  emailType: EmailType
  email: string
  onClose?: () => void
  onBack?: () => void
}

export const EmailVerification: FC<Props> = flowMax(
  addDisplayName('EmailVerification'),
  addProps(({ emailType }) => ({
    isVerification: emailType === 'verification',
  })),
  addNavigate,
  addApi,
  addHandlers({
    onResend:
      ({ api, email }) =>
      () => {
        api.auth.createVerificationEmail({ email, source: 'shopperWeb' })
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ email, isVerification, onClose, onBack, onResend, classes, t }) => (
    <div>
      {onBack && (
        <button onClick={onBack} className={classes.backButton}>
          <img src={backButton} alt={t('back')} />
        </button>
      )}
      <h1 className={classes.title}>{t('auth.emailVerification.title')}</h1>
      <div className={classes.message}>
        <div className={classes.privacyMessage}>
          {t('auth.emailVerification.updatingPrivacyTerms')}
        </div>
        <Trans
          t={t}
          i18nKey={
            isVerification
              ? 'auth.emailVerification.verifyMessage'
              : 'auth.recoverPassword.emailMessage'
          }
          values={{ email }}
        >
          <span className={classes.bold} />
        </Trans>
        {isVerification ? (
          <>
            <div className={classes.resend}>
              <Trans
                t={t}
                i18nKey={'auth.emailVerification.resendMessage'}
                values={{ email }}
              >
                <button className={classes.linkText} onClick={onResend} />
              </Trans>
            </div>
            <button
              className={cx(classes.linkText, classes.otherAccount)}
              onClick={onClose}
            >
              {t('auth.emailVerification.otherAccount')}
            </button>
          </>
        ) : (
          <div
            onClick={onBack}
            className={cx(classes.linkText, classes.backToLogin)}
          >
            {t('auth.recoverPassword.backToLogin')}
          </div>
        )}
      </div>
    </div>
  )
)
